import { computed, signal } from '@preact/signals-react';

import { CartApi } from '@/entities/cart/api';
import type { CartItemModel } from '@/entities/cart/model/CartItemModel';
import type { CartProductOptionModel } from '@/entities/cart/model/CartProductOptionModel';
import { type ProductModel } from '@/entities/product/model';
import { productsStore } from '@/entities/product/store/productsStore';
import { restaurantStore } from '@/entities/restaurant/store/restaurantStore';

type CartItemAdd = {
  productOptions: CartProductOptionModel[];
  id: number;
  quantity: number;
};

type UpdatePayload = {
  itemId: number;
  quantity: number;
  productOptions: CartProductOptionModel[];
};

type CalcItemPriceArgs = {
  productId: number;
  menuId: number;
  options: CartProductOptionModel[];
  qty: number;
};

export const calcItemPrice = ({
  productId,
  menuId,
  options,
  qty,
}: CalcItemPriceArgs) => {
  const product = productsStore.products.value.find(
    (p) => p.id === productId && p.menuId === menuId
  );

  if (!product || !product.price) {
    return 0;
  }

  const optionsTotal = options.reduce((sum, opt) => sum + (opt?.price ?? 0), 0);

  return (product.price + optionsTotal) * qty;
};

const rawCartItems = signal<CartItemModel[]>([]);
const cartItems = computed(() =>
  rawCartItems.value.map((cartItem: CartItemModel) => {
    const product = productsStore.products.value.find(
      (p) => p.id === cartItem.productId && p.menuId === cartItem.menu
    );
    if (!product) {
      return cartItem;
    }

    // eslint-disable-next-line no-param-reassign
    cartItem.product = product;

    cartItem.productOptions.map((option: CartProductOptionModel) => {
      const productOption = product.options.find(
        (o) => o.optionId === option.optionId
      );
      // eslint-disable-next-line no-param-reassign
      option.price = productOption?.price ?? 0;
      return option;
    });
    return cartItem;
  })
);

export const cartStore = {
  items: cartItems,

  total: computed(() =>
    cartItems.value.reduce(
      (sum, cartItem) =>
        sum +
        calcItemPrice({
          qty: cartItem.quantity,
          productId: cartItem.productId,
          menuId: cartItem.menu,
          options: cartItem.productOptions,
        }),
      0
    )
  ),

  async loadCart() {
    if (!restaurantStore.cartId) {
      rawCartItems.value = [];
      return [];
    }
    const items = await CartApi.getCart();
    cartStore.setItems(items);
    return items;
  },

  getProduct(cartItem: CartItemModel) {
    return computed(() =>
      productsStore.products.value.find(
        (p) => p.id === cartItem.productId && p.menuId === cartItem.menu
      )
    );
  },

  setItems(items: CartItemModel[]) {
    rawCartItems.value = items.filter((i) => (i.quantity ?? 0) > 0);
  },

  getQty(productId: number) {
    return cartItems.value
      .filter((item) => item.productId === productId)
      .reduce((acc, item) => acc + item.quantity, 0);
  },

  async add(
    product: ProductModel,
    qty: number,
    productOptions: CartProductOptionModel[]
  ) {
    await CartApi.addToCart({
      menu: product.menuId,
      product: product.id,
      quantity: qty,
      productOptions,
    });
    await this.loadCart();
  },

  async updateQtyByProduct(productId: number, newQty: number) {
    const currentQty = this.getQty(productId);
    if (newQty === currentQty) return;

    const delta = currentQty - newQty;
    const itemsOfProduct = cartItems.value.filter(
      (ci) => ci.productId === productId
    );

    if (delta <= 0) {
      const firstItem = itemsOfProduct[0];
      if (!firstItem) return;
      await CartApi.updateCartItem({
        itemId: firstItem.id,
        quantity: newQty,
        productOptions: firstItem.productOptions,
      });
      await this.loadCart();
      return;
    }

    const [updates] = itemsOfProduct.reduce<[UpdatePayload[], number]>(
      ([acc, rem], item) => {
        if (rem <= 0) return [acc, rem];
        const dec = Math.min(rem, item.quantity);
        const nextQty = item.quantity - dec;
        acc.push({
          itemId: item.id,
          quantity: nextQty,
          productOptions: item.productOptions,
        });
        return [acc, rem - dec];
      },
      [[], delta]
    );

    if (updates.length > 0) {
      await Promise.all(updates.map((u) => CartApi.updateCartItem(u)));
    }
    await this.loadCart();
  },

  async updateQty(cartItem: CartItemAdd) {
    await CartApi.updateCartItem({
      itemId: cartItem.id,
      quantity: cartItem.quantity,
      productOptions: cartItem.productOptions,
    });
    await this.loadCart();
  },

  async addToCart(product: ProductModel, cartItem: Omit<CartItemAdd, 'id'>) {
    await this.add(product, cartItem.quantity, cartItem.productOptions);
  },

  async clear() {
    await CartApi.clearCart();
    rawCartItems.value = [];
    await this.loadCart();
  },
};
